import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import HomeGroupCard from "../components/HomeGroupCard";
import TopCard from "../components/TopCard";
import HomePlayCard from "../components/HomePlayCard";
import ScheduleSearchSection from "../components/ScheduleSearchSection";
import YoutubeVideoGallery from "../components/YoutubeVideoGallery";
import BasicHomePage from "../components/BasicHomePage";
import { getCachedSiteConfig } from "../utils/homePageCache";

const HomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [homePageMode, setHomePageMode] = useState<string>("basic");

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getCachedSiteConfig();
        setHomePageMode(config.homePageMode);
      } catch (error) {
        console.error("Failed to load site configuration:", error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" style={{ color: "#810081" }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  // Render basic mode if configured
  if (homePageMode === "basic") {
    return <BasicHomePage />;
  }

  // Otherwise render advanced mode (default)
  return (
    <div>
      {/* Hero Section */}
      <TopCard />
      
      {/* Welcome Section */}
      <section className="py-5" style={{ background: "linear-gradient(135deg, #FFD600 0%, #810081 100%)" }}>
        <Container>
          <Row className="text-center">
            <Col lg={10} className="mx-auto">
              <div className="bg-white rounded-3 shadow-lg p-5">
                <h2 className="display-4 fw-bold mb-4" style={{ color: "#810081" }}>
                  Welcome to Natakiya
                </h2>
                <p className="lead text-muted mb-4" style={{ fontSize: "1.2rem", lineHeight: "1.6" }}>
                  Discover the magic of theater! Connect with talented groups, explore captivating plays, 
                  and book your next unforgettable cultural experience.
                </p>
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <Link 
                    to="/plays" 
                    className="btn btn-lg px-4 py-3 fw-bold shadow"
                    style={{ backgroundColor: "#FFD600", color: "#810081", border: "none" }}
                  >
                    <i className="bi bi-play-circle me-2"></i>Browse Plays
                  </Link>
                  <Link 
                    to="/groups" 
                    className="btn btn-lg px-4 py-3 fw-bold"
                    style={{ backgroundColor: "transparent", color: "#810081", border: "2px solid #810081" }}
                  >
                    <i className="bi bi-people me-2"></i>Explore Groups
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Schedule Search Section */}
      <ScheduleSearchSection />

      {/* Featured Groups Section */}
      <section className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h3 className="display-6 fw-bold mb-3" style={{ color: "#810081" }}>
              Featured Theater Groups
            </h3>
            <p className="lead text-muted mb-4">Meet the talented artists bringing stories to life</p>
            <div className="mx-auto" style={{ width: "80px", height: "3px", background: "linear-gradient(45deg, #810081, #FFD600)", borderRadius: "2px" }}></div>
          </div>
          <HomeGroupCard useCached={true} />
          <div className="text-center mt-4">
            <Link 
              to="/groups" 
              className="btn btn-lg px-4"
              style={{ backgroundColor: "transparent", color: "#810081", border: "2px solid #810081" }}
            >
              View All Groups <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </div>
        </Container>
      </section>

      {/* Latest Plays Section */}
      <section className="py-5" style={{ background: "linear-gradient(135deg, #810081 0%, #FFD600 100%)" }}>
        <Container>
          <div className="text-center mb-5">
            <h3 className="display-6 fw-bold mb-3 text-white">
              Latest Productions
            </h3>
            <p className="lead mb-4 text-white">Don't miss these amazing theatrical experiences</p>
            <div className="mx-auto" style={{ width: "80px", height: "3px", background: "rgba(255,255,255,0.8)", borderRadius: "2px" }}></div>
          </div>
          <HomePlayCard useCached={true} />
          <div className="text-center mt-4">
            <Link 
              to="/plays" 
              className="btn btn-lg px-4 shadow"
              style={{ backgroundColor: "#FFD600", color: "#810081", border: "none" }}
            >
              Discover More Plays <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </div>
        </Container>
      </section>

      {/* Featured Videos Section */}
      <section className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h3 className="display-6 fw-bold mb-3" style={{ color: "#810081" }}>
              Featured Videos
            </h3>
            <p className="lead text-muted mb-4">Watch our special performances and highlights</p>
            <div className="mx-auto" style={{ width: "80px", height: "3px", background: "linear-gradient(45deg, #810081, #FFD600)", borderRadius: "2px" }}></div>
          </div>
          <YoutubeVideoGallery showSpecialOnly={true} limit={4} useCached={true} />
        </Container>
      </section>

      {/* Call to Action Section */}
      {/* <section className="py-5 bg-warning text-dark">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h3 className="display-6 fw-bold mb-3">Ready to Experience Theater?</h3>
              <p className="lead mb-4">
                Join thousands of theater enthusiasts and book your tickets today!
              </p>
              <Link to="/signing" className="btn btn-dark btn-lg px-5">
                Get Started Now
              </Link>
            </Col>
          </Row>
        </Container>
      </section> */}
    </div>
  );
};

export default HomePage;
